// components/ValidationInterruptionBDC.tsx
// Validation des demandes d'interruption de bon de caisse — quatre-yeux :
// le validateur doit être différent du demandeur.
// Liste pure : les montants sont calculés à la demande — ici on ne voit
// que les demandes en attente ; clic sur une ligne -> aperçu -> Valider
// (recalcul serveur à la date de validation) ou Rejeter.
import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Space, Spin, Table, Tabs } from "antd";
import type { TabsProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { UserOutlined, EyeOutlined, FilePdfOutlined } from '@ant-design/icons';
import {
  Typography, Box,
} from "@mui/material";
import axios from "axios";
import { IDemandeInterBDC } from "../../../dataTypes/GlobalDataType";
import { Crypter } from "../../../Functions/Security/Crypter";
import Data from '../../../Data/Data.json';
import { useAuthHeader, useAuthUser } from '../../../Services/Auth/sessionAuth';
import Utilisateur from "../../../Models/Utilisateur";
import { fetchDataTable2, GlobalExportToCSV, GlobalExportToExcel, GlobalExportToJSON, GlobalExportToPDF, GlobalExportToWord } from "../../../utilitaires/globalFunction";
import formatNumber from "../../../Functions/Traitement/FormatNumber";
import SweetAlert from "sweetalert2";
import ExportMenu from "../../ExportMenu";
import ApercuInterruptionBDC, { IApercuInterruption } from "./ApercuInterruptionBDC";

export default function ValidationInterruptionBDC() {
  const auth = useAuthUser()
  const authHeader = useAuthHeader()
  const Perss: Utilisateur = JSON.parse(JSON.stringify(auth()))

  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<IDemandeInterBDC | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  // Aperçu recalculé par le serveur à la journée comptable (identique à
  // ce que la validation comptabilisera)
  const [apercu, setApercu] = useState<IApercuInterruption | null>(null);

  // Quatre-yeux : la demande sélectionnée a été créée par l'utilisateur
  // courant -> il ne peut pas la valider lui-même
  const isDemandeur = !!selected && String(selected.UTIDEM || '').toLowerCase() === String(Perss.coduti || '').toLowerCase();

  // Liste des demandes EN ATTENTE (cdetat '01')
  const [listeDemandes, setListeDemandes] = useState<IDemandeInterBDC[]>([]);
  const fetchListeDemandes = async () => {
    setLoading(true);
    try {
      var params = { CodeAgence: Perss.codage, Table: "DEMANDEINTERBDC", Colonne1: "NUMDEM", Colonne2: "NOMCLI", Colagence: "CODAGE", Init: "", choix: 32, colfiltre: "", valfiltre: "" }
      const res = await fetchDataTable2(Data.ApiUrl.AnySearch, params, authHeader());
      setListeDemandes((res || []).filter((d: IDemandeInterBDC) => d.CDETAT === '01'));
    } catch (err) {
      console.error("Erreur de chargement:", err);
    } finally {
      setLoading(false);
    }
  };

  // Documents uploadés sur la demande sélectionnée (DEMANDEINTERBDC_DOC) —
  // endpoint dédié (filtré serveur) : la recherche générique AnySearch ne
  // fonctionne pas sur cette table (colonne PIECEJOINTE VARCHAR(MAX)).
  const [docsDemande, setDocsDemande] = useState<any[]>([]);
  const fetchDocsDemande = async (numDem?: string) => {
    if (!numDem) { setDocsDemande([]); return; }
    try {
      const response = await axios({
        method: 'get',
        maxBodyLength: Infinity,
        url: Data.ApiUrl.GetDocsInterruptionBDC,
        headers: {
          'CypherNumDem': Crypter.encrypt(JSON.stringify(numDem)),
          'CypherAgence': Crypter.encrypt(JSON.stringify(Perss.codage)),
          'Authorization': authHeader(),
          'Content-Type': 'application/json'
        }
      });
      if (response.data) {
        const res = JSON.parse(Crypter.decrypt(response.data));
        setDocsDemande(res?.Result?.Table || []);
      }
    } catch (err) {
      console.error("Erreur de chargement:", err);
      setDocsDemande([]);
    }
  };

  // Sélection d'une demande (radio ou clic de ligne) -> charge ses docs
  const selectDemande = (record: IDemandeInterBDC) => {
    setSelected(record);
    setSelectedRowKeys([String(record.NUMDEM)]);
    fetchDocsDemande(record.NUMDEM);
  };

  useEffect(() => { fetchListeDemandes(); }, [])

  function fmtDate(d: any) {
    if (!d) return '';
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? '' : dt.toLocaleDateString('fr-FR');
  }

  const columnsDemandes: ColumnsType<IDemandeInterBDC> = [
    { title: 'N° demande', dataIndex: 'NUMDEM', key: 'NUMDEM', width: '13%' },
    { title: 'N° BDC', dataIndex: 'NUMBDC', key: 'NUMBDC', width: '11%' },
    { title: 'Client', dataIndex: 'NOMCLI', key: 'NOMCLI', width: '20%' },
    { title: 'Type', dataIndex: 'TYPBDC', key: 'TYPBDC', width: '8%' },
    { title: 'Montant', dataIndex: 'MONTANT', key: 'MONTANT', width: '10%', align: 'right', render: (v: any) => formatNumber(Number(v) || 0) },
    { title: 'Date demande', dataIndex: 'DATDEM', key: 'DATDEM', width: '9%', render: (v: any) => fmtDate(v) },
    { title: 'Date interruption', dataIndex: 'DATINTERUP', key: 'DATINTERUP', width: '10%', render: (v: any) => fmtDate(v) },
    { title: 'Motif', dataIndex: 'LIBMOTIF', key: 'LIBMOTIF', width: '11%' },
    { title: 'Demandeur', dataIndex: 'UTIDEM', key: 'UTIDEM', width: '8%' },
    { title: 'Net client', dataIndex: 'NETCLIENT', key: 'NETCLIENT', width: '10%', align: 'right', render: (v: any) => formatNumber(Number(v) || 0) },
  ];

  // Colonnes des documents joints à la demande — clic œil pour
  // visualiser le fichier (base64 ouvert dans un nouvel onglet)
  const columnsDocs: ColumnsType<any> = [
    { title: 'Libellé', dataIndex: 'LIBDOC', key: 'LIBDOC', width: '30%' },
    { title: 'Fichier', dataIndex: 'NOMFICHIER', key: 'NOMFICHIER', width: '35%' },
    { title: 'Saisi par', dataIndex: 'UTISAI', key: 'UTISAI', width: '12%' },
    { title: 'Date', dataIndex: 'DATCRE', key: 'DATCRE', width: '13%', render: (v: any) => fmtDate(v) },
    {
      title: '', key: 'voir', width: '10%',
      render: (_, record: any) => (
        <Button
          size='small'
          icon={<EyeOutlined />}
          onClick={() => {
            if (!record.PIECEJOINTE) return;
            const win = window.open();
            win?.document.write(`<iframe src="${record.PIECEJOINTE}" style="width:100%;height:100vh;border:0"></iframe>`);
          }}
        >
          Voir
        </Button>
      ),
    },
  ];

  const handleExport = (format: string) => {
    const title = "Exportation demandes interruption BDC";
    switch (format) {
      case 'excel': GlobalExportToExcel(listeDemandes, title, "ValidationInterruptionBDC"); break;
      case 'pdf': GlobalExportToPDF(listeDemandes, title, "ValidationInterruptionBDC"); break;
      case 'word': GlobalExportToWord(listeDemandes, title, "ValidationInterruptionBDC"); break;
      case 'csv': GlobalExportToCSV(listeDemandes, title, "ValidationInterruptionBDC"); break;
      case 'json': GlobalExportToJSON(listeDemandes, title, "ValidationInterruptionBDC"); break;
    }
  };

  // ------------------------------------------------------------------
  // Validation — quatre-yeux (validateur != demandeur). L'aperçu est
  // recalculé par le serveur à la journée comptable : montants, taxes et
  // contrôles affichés sont exactement ceux de la comptabilisation.
  // ------------------------------------------------------------------
  const ouvrirApercu = async () => {
    if (!selected || selected.CDETAT !== '01') {
      SweetAlert.fire({ icon: "warning", title: "Validation interruption", text: "Sélectionnez une demande en attente dans la liste." });
      return;
    }

    if (String(selected.UTIDEM).toLowerCase() === String(Perss.coduti).toLowerCase()) {
      SweetAlert.fire({
        icon: "warning",
        title: "Avertissement",
        text: `C'est vous (${Perss.nomuti}) qui avez créé cette demande (${selected.NUMDEM}) : un autre utilisateur doit la valider.`,
      });
      return;
    }

    setLoading(true);
    try {
      const payload: IDemandeInterBDC = { NUMDEM: selected.NUMDEM, NUMBDC: selected.NUMBDC, CODAGE: Perss.codage, UTIVAL: Perss.coduti };
      const response = await axios({
        method: 'get',
        maxBodyLength: Infinity,
        url: Data.ApiUrl.ApercuInterruptionBDC,
        headers: {
          'CypherDemandeInter': Crypter.encrypt(JSON.stringify(payload)),
          'Authorization': authHeader(),
          'Content-Type': 'application/json'
        }
      });
      const res = response.data ? JSON.parse(Crypter.decrypt(response.data)) : null;
      if (res?.StatutCode == 200 && res.Result) {
        setApercu(res.Result);
        setPreviewOpen(true);
      } else {
        SweetAlert.fire({ icon: "warning", title: "Aperçu interruption", text: res?.Message || "Calcul indisponible" });
      }
    } catch (err) {
      SweetAlert.fire({ icon: "error", title: "Aperçu interruption", text: String(err) });
    } finally {
      setLoading(false);
    }
  };

  const onValider = async () => {
    setPreviewOpen(false);
    setLoading(true);
    const demande: IDemandeInterBDC = {
      NUMDEM: selected?.NUMDEM,
      NUMBDC: selected?.NUMBDC,
      CODAGE: Perss.codage,
      UTIVAL: Perss.coduti,
    };
    let data = Crypter.encrypt(JSON.stringify(demande));
    let config = {
      method: 'put',
      maxBodyLength: Infinity,
      url: Data.ApiUrl.ValiderInterruptionBDC,
      headers: {
        'CypherDemandeInter': data,
        'Authorization': authHeader(),
        'Content-Type': 'application/json'
      },
      data: data
    };

    axios.request(config)
      .then((response) => {
        setLoading(false);
        if (response.data) {
          const res = JSON.parse(Crypter.decrypt(response.data));
          if (res.StatutCode == 200) {
            setSelected(null); setSelectedRowKeys([]); setDocsDemande([]);
            fetchListeDemandes();
            SweetAlert.fire({ title: "Interruption validée", text: res.Result, icon: "success" })
          }
          else {
            SweetAlert.fire({ title: "Warning", text: res.Message, icon: "warning" })
          }
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log(error)
        SweetAlert.fire({ title: "Error Front", text: String(error), icon: "error" })
      });
  };

  // Rejet de la demande — motif obligatoire (OBSERVATION)
  const onRejeter = async () => {
    if (!selected || selected.CDETAT !== '01') {
      SweetAlert.fire({ icon: "warning", title: "Rejet interruption", text: "Sélectionnez une demande en attente dans la liste." });
      return;
    }
    const result = await SweetAlert.fire({
      title: "Rejeter la demande " + selected.NUMDEM + " ?",
      input: 'text',
      inputPlaceholder: 'Motif du rejet (obligatoire)',
      showCancelButton: true,
      confirmButtonText: "Rejeter",
      cancelButtonText: "Annuler",
      icon: "warning",
      confirmButtonColor: "darkred",
      inputValidator: (v: string) => !v ? 'Le motif du rejet est obligatoire' : undefined,
    } as any);

    if (!result.isConfirmed) return;

    setLoading(true);
    const demande: IDemandeInterBDC = {
      NUMDEM: selected.NUMDEM,
      NUMBDC: selected.NUMBDC,
      CODAGE: Perss.codage,
      UTIVAL: Perss.coduti,
      OBSERVATION: result.value || '',
    };
    let data = Crypter.encrypt(JSON.stringify(demande));
    let config = {
      method: 'put',
      maxBodyLength: Infinity,
      url: Data.ApiUrl.RejeterInterruptionBDC,
      headers: {
        'CypherDemandeInter': data,
        'Authorization': authHeader(),
        'Content-Type': 'application/json'
      },
      data: data
    };

    axios.request(config)
      .then((response) => {
        setLoading(false);
        if (response.data) {
          const res = JSON.parse(Crypter.decrypt(response.data));
          if (res.StatutCode == 200) {
            setSelected(null); setSelectedRowKeys([]); setDocsDemande([]);
            fetchListeDemandes();
            SweetAlert.fire({ title: "Demande rejetée", text: res.Result, icon: "success" })
          }
          else {
            SweetAlert.fire({ title: "Warning", text: res.Message, icon: "warning" })
          }
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log(error)
        SweetAlert.fire({ title: "Error Front", text: String(error), icon: "error" })
      });
  };

  // En-tête de la demande (le détail des montants vient de l'aperçu serveur)
  const enteteDemande: any[][] = !selected ? [] : [
    ['N° demande', selected.NUMDEM], ['Demandeur', selected.UTIDEM],
    ['Date demande', fmtDate(selected.DATDEM)], ['Date demandée', fmtDate(selected.DATINTERUP)],
    ['Motif', selected.LIBMOTIF || selected.MOTIF], ['Observation', selected.OBSERVATION || '—'],
    ['Net client à la demande', formatNumber(Number(selected.NETCLIENT) || 0)],
    ['Documents joints', docsDemande.length],
  ];

  // ------------------------------------------------------------------
  // Onglet liste des demandes en attente (charte application)
  // ------------------------------------------------------------------
  const items: TabsProps['items'] = [
    {
      key: '1',
      label: (
        <span id='Tab'>
          <UserOutlined />
          Demandes en attente
        </span>
      ),
      children: (
        <div className='tabItem'>
          <div>
            <Box fontStyle="inherit" className='border' width={"85%"} position={"absolute"}>
              <Typography color={"#116599"} sx={{ mb: 1 }}>
                <b>Demandes d'interruption en attente — cliquez une ligne pour sélectionner</b>
              </Typography>
              <Table
                columns={columnsDemandes}
                dataSource={listeDemandes}
                rowKey={(r) => String(r.NUMDEM)}
                size='small'
                pagination={{ pageSize: 10 }}
                rowSelection={{
                  type: 'radio',
                  selectedRowKeys,
                  onChange: (keys: React.Key[]) => {
                    const rec = listeDemandes.find((d: IDemandeInterBDC) => String(d.NUMDEM) === String(keys[0]));
                    if (rec) selectDemande(rec);
                    else { setSelected(null); setSelectedRowKeys([]); setDocsDemande([]); }
                  },
                }}
                onRow={(record) => ({
                  onClick: () => selectDemande(record),
                  style: { cursor: 'pointer' },
                })}
              />

              {/* Quatre-yeux : le demandeur ne peut pas valider sa
                  propre demande — avertissement visible dès la sélection */}
              {isDemandeur && (
                <Alert type="warning" showIcon style={{ marginTop: 8 }}
                  message="Validation par un autre utilisateur requise"
                  description={`C'est vous (${Perss.nomuti}) qui avez créé cette demande (${selected?.NUMDEM}) : un autre utilisateur doit la valider.`} />
              )}

              {/* Documents de la demande sélectionnée — s'affichent
                  dès qu'une ligne est choisie (Safe Care) */}
              {selected && (
                <>
                  <Typography color={"#116599"} sx={{ mt: 2, mb: 1 }}>
                    <b>Documents de la demande {selected.NUMDEM}</b>
                  </Typography>
                  <Table
                    columns={columnsDocs}
                    dataSource={docsDemande}
                    rowKey={(r) => String(r.IDDOC)}
                    size='small'
                    pagination={false}
                    locale={{ emptyText: 'Aucun document joint à cette demande' }}
                  />
                </>
              )}
            </Box>
          </div>
        </div>
      ),
    }
  ];

  return (
    <div className='tab'>
      <Typography align='center' className='formTitle'> <b> VALIDATION INTERRUPTION BON DE CAISSE </b> </Typography>
      <Spin spinning={loading} size='large' style={{ color: '#116599' }}>
        <Form autoComplete="off" labelAlign='left' style={{ minWidth: 'auto', maxWidth: 'auto' }}>
          <div className='bG'>
            <legend><Typography color={"#116599"}>Actions</Typography></legend>
            <Form.Item shouldUpdate className='bouttong'>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button size='large' className='bt_add' onClick={() => { fetchListeDemandes(); }} type="ghost" htmlType="button" block  >
                  Actualiser
                </Button>
                <ExportMenu disabled={listeDemandes.length === 0} onExport={handleExport} />
                <Button size='large' className='bt_add' disabled={!selected || isDemandeur} type="ghost" onClick={ouvrirApercu} htmlType="button" block>
                  Valider
                </Button>
                <Button size='large' className='bt_add' disabled={!selected} type="ghost" onClick={onRejeter} htmlType="button" block>
                  Rejeter
                </Button>
                <Button size='large' onClick={() => { setSelected(null); setSelectedRowKeys([]); setDocsDemande([]); }} className='bt_add' type="ghost" htmlType="button" block>
                  Annuler
                </Button>
              </Space>
            </Form.Item>
          </div>
          <div className='clientP'>
            <div>
              <div >
                <Tabs size='small' type="card" defaultActiveKey="1" items={items} />
              </div>
            </div>
            <div>
            </div>
          </div>
        </Form>
      </Spin>

      {/* Aperçu de la demande avant validation — même gabarit que la
          validation BDC (antd Modal) */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 600, color: 'var(--darkblue)' }}>
            <EyeOutlined style={{ color: 'var(--darkColor)' }} />
            Aperçu — validation interruption {selected?.NUMDEM}
          </div>
        }
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        width="90%"
        centered
        zIndex={2050}
        style={{ maxWidth: '1400px' }}
        footer={[
          <Button key="cancel" onClick={() => setPreviewOpen(false)}>Annuler</Button>,
          <Button key="ok" type="primary" onClick={onValider} loading={loading}
            disabled={!apercu || (apercu.BLOQUANTS || []).length > 0} style={{ background: '#116599' }}>
            Confirmer et valider
          </Button>
        ]}
        bodyStyle={{ maxHeight: 'calc(100vh - 160px)', overflow: 'auto', padding: 16 }}
      >
        {selected && apercu && <ApercuInterruptionBDC apercu={apercu} entete={enteteDemande} />}
      </Modal>
    </div>
  );
}
